import React, { useRef, useMemo, useImperativeHandle, forwardRef } from "react";
import * as THREE from "three";
import type { SatelliteData } from "../earth-globe-r3f";
import { buildSatelliteConfigs } from "./satellite-utils";
import Satellite, { SatelliteRef } from "./satellite";

interface SatelliteSystemProps {
  satelliteData: SatelliteData[] | undefined;
  initialRotation: number;
  speedFactor: number;
}

export interface SatelliteSystemRef {
  getSatelliteMesh: (_index: number) => THREE.Mesh | null;
  getCount: () => number;
}

/**
 * Componente contenedor que gestiona múltiples satélites.
 * Expone acceso a las mallas de satélites mediante un patrón de handle imperativo de dos capas:
 * 1. Cada hijo Satellite expone su malla via getMesh()
 * 2. SatelliteSystem recopila estas refs y las re-expone via getSatelliteMesh(index)
 */
const SatelliteSystem = forwardRef<SatelliteSystemRef, SatelliteSystemProps>(
  ({ satelliteData, initialRotation, speedFactor }, ref) => {
    // Generar configuraciones de satélites (memoizado para rendimiento)
    const configs = useMemo(
      () => buildSatelliteConfigs(satelliteData, initialRotation),
      [satelliteData, initialRotation]
    );

    // Almacenar refs a cada hijo Satellite
    const satelliteRefs = useRef<Array<SatelliteRef | null>>([]);

    // Exponer métodos de acceso a satélites al componente padre
    useImperativeHandle(ref, () => ({
      getSatelliteMesh: (index: number): THREE.Mesh | null => {
        return satelliteRefs.current[index]?.getMesh() ?? null;
      },
      getCount: () => configs.length,
    }));

    return (
      <>
        {configs.map((config, i) => (
          <Satellite
            key={config.name}
            ref={(handle) => {
              satelliteRefs.current[i] = handle;
            }}
            config={config}
            speedFactor={speedFactor}
          />
        ))}
      </>
    );
  }
);

SatelliteSystem.displayName = "SatelliteSystem";

export default SatelliteSystem;
